import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { adminApi } from '../../services/api';
import { useTheme } from '../../components/redux/hooks/useTheme';
import { getEChartsTheme, getLineChartOption, getBarChartOption, getDonutChartOption } from '../../utils/echartsConfig';
import { getDateRangePreset, getFilteredPresets, isValidDateRange } from '../../utils/calendarUtils';
import { convertAnalyticsTimestamps, COMMON_TIMEZONES, getTimezoneLabel } from '../../utils/timezoneUtils';

const RetellAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilterWarning, setDateFilterWarning] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);

  // Organization/Credential selection
  const [credentials, setCredentials] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // Agent selection
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [groupAgents, setGroupAgents] = useState(false);

  // Timezone selection
  const [selectedTimezone, setSelectedTimezone] = useState('auto');

  // Get current theme from Redux
  const { theme } = useTheme();
  const chartTheme = getEChartsTheme(theme.mode);

  // Filtered presets for this dashboard
  const dateRangePresets = getFilteredPresets('all');

  // Load credentials and agents on mount
  useEffect(() => {
    loadCredentials();
    loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCredentials = async () => {
    try {
      const response = await adminApi.voiceaiGetCredentials();
      if (response.data.success) {
        const activeCredentials = response.data.credentials.filter(c => c.is_active);
        setCredentials(activeCredentials);

        // Auto-select first active credential if available
        if (activeCredentials.length > 0) {
          setSelectedOrgId(activeCredentials[0].org_id);
        }
      }
    } catch (err) {
      console.error('Failed to load credentials:', err);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await adminApi.getRetellAgents();
      if (response.data.success && response.data.data) {
        // Filter to only active agents and sort alphabetically by name
        const activeAgents = response.data.data
          .filter(agent => agent.is_active)
          .sort((a, b) => {
            const nameA = (a.agent_name || a.agent_id).toLowerCase();
            const nameB = (b.agent_name || b.agent_id).toLowerCase();
            return nameA.localeCompare(nameB);
          });

        setAgents(activeAgents);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  // Load dashboard when selectedOrgId, selectedAgentId, or groupAgents changes
  useEffect(() => {
    if (selectedOrgId) {
      loadDashboard();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, selectedAgentId, groupAgents]);

  // Apply date range preset
  const applyPreset = async (presetValue) => {
    const { startDate, endDate } = getDateRangePreset(presetValue);
    setDateFrom(startDate);
    setDateTo(endDate);
    setSelectedPreset(presetValue);

    // Immediately load data with new dates
    setLoading(true);
    setError(null);
    try {
      const params = {
        org_id: selectedOrgId,
        date_from: startDate,
        date_to: endDate
      };
      if (selectedAgentId) {
        params.agent_id = selectedAgentId;
      }
      if (groupAgents) {
        params.group_agent_versions = 'true';
      }
      const response = await adminApi.voiceaiGetAnalytics(params);
      if (response.data.success) {
        // Convert UTC timestamps to selected timezone for display
        const convertedData = convertAnalyticsTimestamps(response.data, selectedTimezone);
        setDashboardData(convertedData);
        // Check for date filter error
        if (response.data.date_range?.error) {
          setDateFilterWarning(response.data.date_range.error);
        } else {
          setDateFilterWarning(null);
        }
      } else if (response.data.requires_reauth) {
        setError('Authentication required. Please add credentials for this organization in the Credentials Manager.');
        setDashboardData(null);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      // Check if 401 error - JWT expired
      if (err.response?.status === 401 || err.response?.data?.requires_reauth) {
        setError('Authentication required. Please add credentials for this organization in the Credentials Manager.');
        setDashboardData(null);
      } else {
        setError('Failed to load analytics data. Please try again.');
      }
      console.error('Preset apply error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = useCallback(async () => {
    if (!selectedOrgId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        org_id: selectedOrgId
      };
      if (dateFrom && dateTo) {
        params.date_from = dateFrom;
        params.date_to = dateTo;
      }
      if (selectedAgentId) {
        params.agent_id = selectedAgentId;
      }
      if (groupAgents) {
        params.group_agent_versions = 'true';
      }

      const response = await adminApi.voiceaiGetAnalytics(params);

      if (response.data.success) {
        // Convert UTC timestamps to selected timezone for display
        const convertedData = convertAnalyticsTimestamps(response.data, selectedTimezone);
        setDashboardData(convertedData);
        // Check for date filter error
        if (response.data.date_range?.error) {
          setDateFilterWarning(response.data.date_range.error);
        } else {
          setDateFilterWarning(null);
        }
      } else if (response.data.requires_reauth) {
        setError('Authentication required. Please add credentials for this organization in the Credentials Manager.');
        setDashboardData(null);
      } else {
        setError('Failed to load dashboard data');
      }
    } catch (err) {
      // Check if 401 error - JWT expired
      if (err.response?.status === 401 || err.response?.data?.requires_reauth) {
        setError('Authentication required. Please add credentials for this organization in the Credentials Manager.');
        setDashboardData(null);
      } else {
        setError('Failed to load analytics data. Please try again.');
      }
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, dateFrom, dateTo, selectedAgentId, groupAgents, selectedTimezone]);

  // Format number for display
  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(0);
  };

  // Format duration (ms to readable format)
  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Render KPI Card with trend indicator
  const renderKPICard = (title, value, icon, color, formatter = formatNumber, trend = null) => {
    // Determine trend color and icon
    const getTrendDisplay = (trendValue) => {
      if (trendValue === null || trendValue === undefined) return null;

      const isPositive = trendValue > 0;
      const isNegative = trendValue < 0;
      const trendColor = isPositive ? 'success' : isNegative ? 'danger' : 'secondary';
      const trendIcon = isPositive ? 'mdi mdi-arrow-up' : isNegative ? 'mdi mdi-arrow-down' : 'mdi mdi-minus';

      return (
        <span className={`badge bg-${trendColor}-subtle text-${trendColor} font-size-12 ms-2`}>
          <i className={`${trendIcon} me-1`}></i>
          {Math.abs(trendValue)}%
        </span>
      );
    };

    return (
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="text-muted mb-2">{title}</h6>
              <div className="d-flex align-items-center">
                <h3 className="mb-0">{formatter(value)}</h3>
                {getTrendDisplay(trend)}
              </div>
            </div>
            <div className={`avatar-sm rounded-circle bg-${color} d-flex align-items-center justify-content-center`}>
              <i className={`${icon} text-white font-size-24`}></i>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Page Title & Organization Selector - Always Visible */}
      <div className="row mb-2">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0 font-size-18 me-3">Voice AI Analytics Dashboard</h4>
            <button className="btn btn-outline-secondary" onClick={() => navigate('/admin/voiceai/credentials')}>
              <i className="fas fa-cog me-2"></i>Manage Credentials
            </button>
          </div>            
          <p className="text-muted mb-5">Granular analytics for call trends and system performance KPIs</p>

          <div className="d-flex justify-content-between align-items-start p-3 border-1 mb-3">
            <div className="d-flex gap-5 flex-grow-1">
              {/* Organization Selector */}
              <div className="d-flex flex-column">
                <label htmlFor="orgSelector" className="form-label fw-bold mb-2">
                  Organization:
                </label>
                <div className="d-flex gap-2 align-items-center mb-1">
                  <select
                    id="orgSelector"
                    className="form-select"
                    style={{ width: '210px' }}
                    value={selectedOrgId || ''}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    disabled={credentials.length === 0}
                  >
                    <option value="">-- Select Organization --</option>
                    {credentials.map((cred) => (
                      <option key={cred.id} value={cred.org_id}>
                        {cred.org_id} ({cred.voiceai_username})
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={loadCredentials}
                    title="Refresh credentials list"
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
                {selectedOrgId && (
                  <small className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    Using stored credentials for {selectedOrgId}
                  </small>
                )}
              </div>

              {/* Agent Selector */}
              <div className="d-flex flex-column">
                <label htmlFor="agentSelector" className="form-label fw-bold mb-2">
                  Agent:
                </label>
                <div className="d-flex gap-2 align-items-center mb-1">
                  <select
                    id="agentSelector"
                    className="form-select"
                    style={{ width: '280px' }}
                    value={selectedAgentId || ''}
                    onChange={(e) => setSelectedAgentId(e.target.value || null)}
                    disabled={agents.length === 0}
                  >
                    <option value="">-- All Agents --</option>
                    {agents.map((agent) => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.agent_name || agent.agent_id}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={loadAgents}
                    title="Refresh agents list"
                  >
                    <i className="fas fa-sync-alt"></i>
                  </button>
                </div>
                <small className="text-muted">
                  <i className="fas fa-filter me-1"></i>
                  {selectedAgentId ? 'Filtering analytics for selected agent only' : 'This is optional'}
                </small>
              </div>

              {/* Timezone Selector */}
              <div className="d-flex flex-column">
                <label htmlFor="timezoneSelector" className="form-label fw-bold mb-2">
                  Timezone:
                </label>
                <div className="d-flex gap-2 align-items-center mb-1">
                  <select
                    id="timezoneSelector"
                    className="form-select"
                    style={{ width: '240px' }}
                    value={selectedTimezone}
                    
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                  >
                    {COMMON_TIMEZONES.map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedTimezone === 'auto' && (
                  <small className="text-muted">
                    <i className="fas fa-globe me-1"></i>
                    {getTimezoneLabel(selectedTimezone)}
                  </small>
                )}
              </div>
            </div>

            <div className="page-title-box" style={{ minWidth: 'fit-content' }}>
              {/* Quick Presets */}
              <div className="d-flex gap-2 mb-3 flex-nowrap justify-content-end">
                {dateRangePresets.map(preset => (
                  <button
                    key={preset.value}
                    className={`btn btn-sm ${selectedPreset === preset.value ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => applyPreset(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range */}
              <div className="d-flex gap-2 align-items-center flex-nowrap justify-content-end">
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 'auto' }}
                  value={dateFrom}
                  onChange={(e) => {
                    const newFromDate = e.target.value;
                    if (dateTo && newFromDate && !isValidDateRange(newFromDate, dateTo)) {
                      alert('Start date must be less than or equal to end date');
                      return;
                    }
                    setDateFrom(newFromDate);
                    setSelectedPreset(null);
                  }}
                  placeholder="Start Date"
                />
                <span className="text-muted">to</span>
                <input
                  type="date"
                  className="form-control"
                  style={{ width: 'auto' }}
                  value={dateTo}
                  onChange={(e) => {
                    const newToDate = e.target.value;
                    if (dateFrom && newToDate && !isValidDateRange(dateFrom, newToDate)) {
                      alert('End date must be greater than or equal to start date');
                      return;
                    }
                    setDateTo(newToDate);
                    setSelectedPreset(null);
                  }}
                  placeholder="End Date"
                />
                <button
                  className="btn btn-primary"
                  onClick={loadDashboard}
                  disabled={!dateFrom || !dateTo}
                > Apply
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setDateFrom('');
                    setDateTo('');
                    setSelectedPreset(null);
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Show date filter warning if present */}
      {dateFilterWarning && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-warning alert-dismissible fade show" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Date Filter Warning:</strong> {dateFilterWarning}
              <button
                type="button"
                className="btn-close"
                onClick={() => setDateFilterWarning(null)}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )}

      {/* Show loading state for charts only */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading analytics data...</p>
        </div>
      )}

      {/* Show error state */}
      {!loading && error && (
        <div className="alert alert-danger" role="alert">{error}</div>
      )}

      {/* Show "no credentials" message */}
      {!loading && !error && !dashboardData && credentials.length === 0 && (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          No active credentials found. Please add credentials in the Credentials Manager.
        </div>
      )}

      {/* Show "select org" message */}
      {!loading && !error && !dashboardData && credentials.length > 0 && !selectedOrgId && (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle me-2"></i>
          Please select an organization to view analytics data.
        </div>
      )}

      {/* Render dashboard charts when data is available */}
      {!loading && !error && dashboardData && (() => {
        const { kpis, time_series, concurrency_over_time, distributions, agent_metrics } = dashboardData;

        return (
          <>
            {/* KPI Cards */}
            <div className="row mb-4">
              <div className="col-md-4">
                {renderKPICard('Total Calls', kpis?.total_calls, 'fas fa-phone', 'primary', formatNumber, kpis?.total_calls_trend)}
              </div>
              <div className="col-md-4">
                {renderKPICard('Avg Duration', kpis?.avg_duration_ms, 'fas fa-clock', 'info', formatDuration, kpis?.avg_duration_ms_trend)}
              </div>
              <div className="col-md-4">
                {renderKPICard('Median Latency', kpis?.median_latency_ms, 'fas fa-tachometer-alt', 'warning', (val) => val ? `${val.toFixed(0)}ms` : 'N/A', kpis?.median_latency_ms_trend)}
              </div>
            </div>

            {/* Trend Charts */}
            <div className="row mb-3">
              <div className="col-12">
                <h5 className="mb-3">Call Volume & Performance Trends</h5>
              </div>
            </div>
            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.calls_over_time, 'Calls Over Time', '')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.duration_over_time, 'Average Duration Over Time', '', { valueType: 'duration' })}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.success_rate_over_time, 'Success Rate Over Time', '', { valueType: 'percentage' })}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.latency_over_time, 'Average Latency Over Time', 'Latency (ms)')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.transfer_rate_over_time, 'Transfer Rate Over Time', '', { valueType: 'percentage' })}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.picked_up_rate_over_time, 'Pick-up Rate Over Time', '', { valueType: 'percentage' })}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(time_series?.voicemail_rate_over_time, 'Voicemail Rate Over Time', '', { valueType: 'percentage' })}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Distribution Charts */}
            <div className="row mb-3">
              <div className="col-12">
                <h5 className="mb-3">Call Distributions</h5>
              </div>
            </div>
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getDonutChartOption(distributions?.calls_by_direction, 'By Direction')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getDonutChartOption(distributions?.calls_by_success, 'By Success Status')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getDonutChartOption(distributions?.calls_by_sentiment, 'By Sentiment')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getDonutChartOption(distributions?.calls_by_disconnection, 'By Disconnection')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Agent Performance */}
            <div className="row mb-3">
              <div className="col-12">
                <h5 className="mb-3">Agent Performance</h5>
              </div>
            </div>
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getBarChartOption(agent_metrics?.success_by_agent, 'Success Rate by Agent', '', { valueType: 'percentage' })}
                      style={{ height: '350px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getBarChartOption(agent_metrics?.transfer_by_agent, 'Transfer Rate by Agent', '', { valueType: 'percentage' })}
                      style={{ height: '350px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getBarChartOption(agent_metrics?.picked_up_by_agent, 'Pick-up Rate by Agent', '', { valueType: 'percentage' })}
                      style={{ height: '350px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Concurrency */}
            <div className="row mb-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body">
                    <ReactECharts
                      option={getLineChartOption(concurrency_over_time, 'Call Concurrency Over Time', '')}
                      style={{ height: '300px' }}
                      lazyUpdate={true}
                      theme={chartTheme}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}
    </>
  );
};

export default RetellAnalyticsDashboard;
